import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { isBuildingUnlocked, getUnlockTech } from "../game/data/research";
import { sfxClick, sfxError } from "../game/audio";
import { Zap, Droplet, Wind, Wheat, Layers, Cog, Package, Coins } from "lucide-react";

const RES_ICONS = { Zap, Droplet, Wind, Wheat, Layers, Cog, Package, Coins };

const RESOURCE_LABELS = {
  power: "Power", water: "Clean Water", oxygen: "Oxygen", food: "Food",
  raw_water: "Raw Water", metal_ore: "Metal Ore", refined_metal: "General Metal",
  iron: "Iron", aluminum: "Aluminum", copper: "Copper", silicon: "Silicon",
  regolith: "Regolith", refinery_tailings: "Refinery Tailings", construction_material: "Building Material",
  machine_parts: "Machine Parts", electronic_components: "Electronics",
  utility_components: "Utility Components", structural_components: "Structural Components",
  polymers: "Polymers", processed_minerals: "Processed Minerals", fertilizer: "Fertilizer",
  recyclable_material: "Recyclable Material", medical_supplies: "Medical Supplies", mission_supplies: "Mission Supplies",
  hydrogen: "Hydrogen", methane: "Methane", co2: "CO₂", nitrogen: "Nitrogen", argon: "Argon",
  carbon_monoxide: "CO Process Gas", rocket_propellant: "Rocket Propellant",
  waste: "Waste", organic_waste: "Organic Waste", wastewater: "Wastewater",
  research_points: "Research Points", credits: "Credits", morale: "Morale", health: "Health",
};

const RESOURCE_COLORS = {
  power: "#ffc107", water: "#00e5ff", oxygen: "#7fffd4", food: "#a3e635",
  raw_water: "#0066aa", metal_ore: "#8a4020", refined_metal: "#d85c31",
  iron: "#a76f55", aluminum: "#bfc6ce", copper: "#c97945", silicon: "#8b7f9b",
  regolith: "#a0522d", refinery_tailings: "#76645a", construction_material: "#b98a66",
  machine_parts: "#e8a37e", electronic_components: "#4169e1", utility_components: "#daa520",
  structural_components: "#cd853f", polymers: "#b042ff", processed_minerals: "#b8a58a",
  fertilizer: "#88aa44", recyclable_material: "#8b8d85", medical_supplies: "#ff6688",
  mission_supplies: "#b9c5d5", hydrogen: "#b9e6ff", methane: "#b7f0cf", co2: "#8a8a8a",
  nitrogen: "#87ceeb", argon: "#c2a5ff", carbon_monoxide: "#9a8e82", rocket_propellant: "#ff6347",
  waste: "#554433", organic_waste: "#6b8e23", wastewater: "#556b2f",
  research_points: "#00fa9a", credits: "#00fa9a",
};

const RESOURCE_ICON_KEY = {
  power: "Zap", water: "Droplet", raw_water: "Droplet", wastewater: "Droplet",
  oxygen: "Wind", hydrogen: "Wind", methane: "Wind", co2: "Wind", nitrogen: "Wind", argon: "Wind", carbon_monoxide: "Wind",
  food: "Wheat", fertilizer: "Wheat", refined_metal: "Layers", metal_ore: "Layers", iron: "Layers", aluminum: "Layers",
  copper: "Layers", silicon: "Layers", regolith: "Layers", refinery_tailings: "Layers", processed_minerals: "Layers",
  machine_parts: "Cog", polymers: "Package", electronic_components: "Package", utility_components: "Package",
  structural_components: "Package", construction_material: "Package", recyclable_material: "Package",
  medical_supplies: "Package", mission_supplies: "Package", rocket_propellant: "Package", credits: "Coins",
};

export function ResourcePill({ resId, value, sign = "" }) {
  const color = RESOURCE_COLORS[resId] || "#ffffff";
  const Icon = RES_ICONS[RESOURCE_ICON_KEY[resId]] || Package;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 border bg-black/30 text-[10px] font-mono"
      style={{ borderColor: color + "60", color }}
      title={`${RESOURCE_LABELS[resId] || resId}: ${sign}${value}`}
    >
      <Icon size={12} />
      {sign}{value}
    </span>
  );
}

export function BuildingTooltip({ b }) {
  const accent = b.accent || b.color || "#00e5ff";
  const inputs = b.productionInputs || {};
  const outputs = b.productionOutputs || {};
  const storage = b.storageCapacity || {};
  const hexCount = b.footprintHexes?.length || 1;

  return (
    <div
      className="w-72 p-3 bg-[#0A0C10]/98 border border-white/15 backdrop-blur-md shadow-[0_0_30px_rgba(0,229,255,0.15)]"
      style={{ fontFamily: "Rajdhani, sans-serif" }}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="w-1.5 h-6" style={{ background: accent, boxShadow: `0 0 10px ${accent}` }} />
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
            T{b.tier || 0}{b.upgradeLevels > 1 ? ` · L${b.currentLevel || 1}/${b.upgradeLevels}` : ""} · {hexCount} hex{hexCount > 1 ? "es" : ""}
          </div>
          <div className="text-base font-semibold uppercase tracking-wide text-white">{b.name}</div>
        </div>
      </div>
      <div className="text-xs font-mono text-slate-300 leading-snug mb-3">{b.description}</div>

      <div className="mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1">Build Cost</div>
        <div className="flex flex-wrap gap-1">
          {b.cost && Object.keys(b.cost).length > 0 ? (
            Object.entries(b.cost).map(([k, v]) => <ResourcePill key={k} resId={k} value={v} />)
          ) : (
            <span className="text-[11px] font-mono text-slate-500">Free</span>
          )}
        </div>
        {b.buildTime > 0 && <div className="text-[10px] font-mono text-slate-400 mt-1">Build time: <span className="text-white">{b.buildTime}s</span></div>}
        {b.workersRequired > 0 && <div className="text-[10px] font-mono text-slate-400 mt-0.5">Workers: <span className="text-white">{b.workersRequired}</span></div>}
      </div>

      {b.powerConsumption > 0 && (
        <div className="mb-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1">Power Usage</div>
          <ResourcePill resId="power" value={b.powerConsumption} sign="-" />
        </div>
      )}
      {outputs.power > 0 && (
        <div className="mb-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1">Power Output</div>
          <ResourcePill resId="power" value={outputs.power} sign="+" />
        </div>
      )}

      {Object.keys(inputs).length > 0 && (
        <div className="mb-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1">Consumes</div>
          <div className="flex flex-wrap gap-1">
            {Object.entries(inputs).map(([k, v]) => <ResourcePill key={k} resId={k} value={v} sign="-" />)}
          </div>
        </div>
      )}

      {Object.keys(outputs).filter((k) => k !== "power").length > 0 && (
        <div className="mb-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1">Produces</div>
          <div className="flex flex-wrap gap-1">
            {Object.entries(outputs).filter(([k]) => k !== "power").map(([k, v]) => <ResourcePill key={k} resId={k} value={v} sign="+" />)}
          </div>
        </div>
      )}

      {Object.keys(storage).length > 0 && (
        <div className="mb-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1">Adds Storage</div>
          <div className="flex flex-wrap gap-1">
            {Object.entries(storage).map(([k, v]) => <ResourcePill key={k} resId={k} value={v} sign="+" />)}
          </div>
        </div>
      )}
    </div>
  );
}

export default function BuildingCard({ b, afford, active, unlocked, onSelect }) {
  const accent = b.accent || b.color || "#00e5ff";
  const disabled = !afford || !unlocked;
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(false);
  const [tipPos, setTipPos] = useState({ top: 0, left: 0 });

  const updateTipPos = () => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const tipWidth = 288;
    const wouldOverflow = rect.right + 8 + tipWidth > window.innerWidth;
    setTipPos({ top: rect.top, left: wouldOverflow ? rect.left - tipWidth - 8 : rect.right + 8 });
  };

  return (
    <div
      ref={wrapRef}
      className="relative group w-full"
      onMouseEnter={() => { updateTipPos(); setHover(true); }}
      onMouseLeave={() => setHover(false)}
    >
      <button
        data-testid={`build-${b.id}`}
        onClick={() => {
          if (disabled) { sfxError(); return; }
          sfxClick();
          onSelect(b.id);
        }}
        disabled={disabled}
        className={`w-full text-left p-3 border transition-colors ${
          active
            ? "border-cyan-400 bg-cyan-500/10"
            : afford && unlocked
            ? "border-white/15 bg-white/5 hover:border-cyan-400/60 hover:bg-cyan-500/5"
            : "border-white/10 bg-white/[0.02] opacity-50 cursor-not-allowed"
        }`}
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-2.5 h-2.5 shrink-0" style={{ background: accent, boxShadow: `0 0 8px ${accent}` }} />
          <span className="text-xs font-semibold uppercase tracking-wider text-white truncate">{b.name}</span>
          {b.upgradeLevels > 1 && (
            <span className="text-[9px] font-mono px-1 border border-cyan-400/30 text-cyan-300 ml-auto shrink-0">L{b.currentLevel || 1}/{b.upgradeLevels}</span>
          )}
          {b.tier > 0 && (
            <span className={`text-[9px] font-mono px-1 border border-white/20 text-slate-400 shrink-0 ${b.upgradeLevels > 1 ? "" : "ml-auto"}`}>T{b.tier}</span>
          )}
        </div>
        <div className="flex flex-wrap gap-1">
          {b.cost && Object.entries(b.cost).slice(0, 4).map(([k, v]) => <ResourcePill key={k} resId={k} value={v} />)}
          {b.powerConsumption > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border text-[10px] font-mono" style={{ borderColor: "#ffc10780", color: "#ffc107" }}>
              <Zap size={12} />-{b.powerConsumption}
            </span>
          )}
          {b.productionOutputs?.power > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border text-[10px] font-mono" style={{ borderColor: "#00fa9a80", color: "#00fa9a" }}>
              <Zap size={12} />+{b.productionOutputs.power}
            </span>
          )}
        </div>
        {!unlocked && (
          <div className="text-[9px] font-mono text-slate-600 mt-1 uppercase tracking-widest">🔒 {getUnlockTech(b)?.name || "Locked"}</div>
        )}
      </button>

      {hover && createPortal(
        <div className="fixed z-50 pointer-events-none transition-opacity duration-150" style={{ top: tipPos.top, left: tipPos.left }}>
          <BuildingTooltip b={b} />
        </div>,
        document.body
      )}
    </div>
  );
}

export { isBuildingUnlocked };
